import React, { useState } from 'react';
import { Modal } from '@fullstory/components';
import { Dashboard } from '@fullstory/proto/fs/services/dx-objects';
import { DashboardSelectMenu } from '../DashboardSelectMenu';
import { FormattedMessage, useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';

export const objectTypes = ['metric', 'funnel', 'retention', 'heatmap'] as const;

type DashboardSelectMenuProps = Pick<
  React.ComponentProps<typeof DashboardSelectMenu>,
  'dashboardsLoading' | 'starredDashboards' | 'recentDashboards' | 'otherDashboards'
>;

export interface SaveObjectModalProps extends DashboardSelectMenuProps {
  objectType: (typeof objectTypes)[number];
  onCloseSignal: () => void;
  /**
   * A dashboard may or may not be selected.
   */
  onSave: (dashboard?: Dashboard) => void;
  /**
   * Optionally specify a dashboard to be initially selected.
   */
  initialSelectedDashboard: Dashboard | null;
}

const saveObjectModelMessages: {
  [key in (typeof objectTypes)[number]]: { titlePlaceholder: string; saveButtonText: string };
} = {
  funnel: {
    titlePlaceholder: intl.formatMessage({ defaultMessage: 'Funnel title', id: 'Tv3Z3m' }),
    saveButtonText: intl.formatMessage({ defaultMessage: 'Save Funnel', id: 'wmrKEg' }),
  },
  metric: {
    titlePlaceholder: intl.formatMessage({ defaultMessage: 'Metric title', id: '6B01qI' }),
    saveButtonText: intl.formatMessage({ defaultMessage: 'Save Metric', id: '+cOwXx' }),
  },
  retention: {
    titlePlaceholder: intl.formatMessage({ defaultMessage: 'Retention title', id: 'scnMFX' }),
    saveButtonText: intl.formatMessage({ defaultMessage: 'Save Retention', id: 'NdwKo6' }),
  },
  heatmap: {
    titlePlaceholder: intl.formatMessage({ defaultMessage: 'Heatmap title', id: 'F6eKP1' }),
    saveButtonText: intl.formatMessage({ defaultMessage: 'Save Heatmap', id: 'aEW6IC' }),
  },
};

export const SaveObjectModal: React.FC<SaveObjectModalProps> = ({
  objectType,
  onCloseSignal,
  onSave,
  initialSelectedDashboard,
  ...dashboardsSelectMenuProps
}) => {
  const intl = useIntl();

  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(
    initialSelectedDashboard,
  );

  const handleSave = () => {
    onSave(selectedDashboard || undefined);
  };

  const messages = saveObjectModelMessages[objectType];

  return (
    <Modal data-component="SaveObjectModal" onCloseSignal={onCloseSignal} disableBackdropClick>
      <Modal.Header onCloseSignal={onCloseSignal}>
        <FormattedMessage
          defaultMessage="Save {objectType} to Dashboard"
          id="EcSzd9"
          values={{ objectType }}
        />
      </Modal.Header>
      <form>
        <Modal.Body>
          <DashboardSelectMenu
            {...dashboardsSelectMenuProps}
            id="object-dashboard-menu"
            selected={selectedDashboard || null}
            onChange={dashboard => setSelectedDashboard(dashboard)}
            clearable
          />
        </Modal.Body>
        <Modal.FooterActions
          primaryAction={{ action: handleSave, text: messages.saveButtonText }}
          secondaryAction={{
            action: onCloseSignal,
            text: intl.formatMessage({
              defaultMessage: 'Cancel',
              id: 'JHJyH+',
              description: 'Cancel and close popup',
            }),
          }}
        />
      </form>
    </Modal>
  );
};
